import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation, useLanguage } from '../context/LanguageContext';
import { LOGO_LIGHT } from '../App';
import { Mail, ArrowLeft, Check, Lock, Eye, EyeOff, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import { isNative, browser } from '../lib/native';
import { trackSignUp, trackLogin } from '../lib/analytics';
import { Capacitor } from '@capacitor/core';
import LanguageSelectorBadge from '../components/LanguageSelectorBadge';

const API = API_URL;

// Splash image URL (local for faster loading)
const SPLASH_IMAGE = '/sfondo_login.webp';
// Desktop background image
const DESKTOP_BG_IMAGE = '/sfondo_login.webp';

export default function Login() {
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // Registration flow states
  const [step, setStep] = useState('email'); // 'email', 'code', 'password'
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // Refs for code inputs
  const codeInputRefs = useRef([]);

  // Handle keyboard show/hide for native apps
  useEffect(() => {
    if (!isNative) return;
    if (!Capacitor.isPluginAvailable('Keyboard')) return;
    
    let showListener = null;
    let hideListener = null;
    
    const setupKeyboard = async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
          setKeyboardVisible(true);
          setTimeout(() => {
            const activeElement = document.activeElement;
            if (activeElement && activeElement.tagName === 'INPUT') {
              activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        });
        
        hideListener = await Keyboard.addListener('keyboardWillHide', () => {
          setKeyboardVisible(false);
        });
      } catch (e) {
        console.log('[Keyboard] Plugin not available:', e.message);
      }
    };
    
    setupKeyboard();
    
    return () => {
      if (showListener?.remove) showListener.remove();
      if (hideListener?.remove) hideListener.remove();
    };
  }, []);

  // Fetch public settings to check if Google login is enabled
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/public`);
        setGoogleLoginEnabled(response.data.google_login_enabled);
      } catch (err) {
        setGoogleLoginEnabled(false);
      }
    };
    fetchSettings();
  }, []);

  // Handle email submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !acceptedTerms) return;

    setLoading(true);
    setError('');

    try {
      console.log('[Register] API URL:', API);
      // Use native HTTP client on iOS to bypass CORS
      await httpClient.post(`${API}/api/auth/register-email`, {
        email,
        accepted_terms: acceptedTerms,
        language: language || 'it',
      });
      setStep('code');
    } catch (err) {
      console.error('[Register] Error:', err);
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError(`Errore di rete. Impossibile raggiungere il server.`);
      } else if (err.code === 'ECONNABORTED') {
        setError('Timeout - il server non risponde');
      } else {
        setError(err.response?.data?.detail || t('auth.registrationError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle code input change
  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle code paste
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      codeInputRefs.current[5]?.focus();
    }
  };

  // Handle code backspace
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle code verification
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;

    setLoading(true);
    setError('');

    try {
      await httpClient.post(`${API}/api/auth/verify-code`, {
        email,
        code: fullCode
      });
      setStep('password');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  // Handle password submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    
    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await httpClient.post(`${API}/api/auth/set-password-with-code`, {
        email,
        code: code.join(''),
        password
      });

      // Save session token
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }

      // Track successful signup
      trackSignUp('email');

      // Set user and redirect to dashboard
      setUser(response.data.user);
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.registrationError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      await httpClient.post(`${API}/api/auth/resend-code`, { email });
      setResendSuccess(true);
      setCode(['', '', '', '', '', '']);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.registrationError'));
    } finally {
      setResendLoading(false);
    }
  };

  // Go back to previous step
  const goBack = () => {
    setError('');
    if (step === 'code') {
      setStep('email');
      setCode(['', '', '', '', '', '']);
    } else if (step === 'password') {
      setStep('code');
      setPassword('');
      setConfirmPassword('');
    }
  };

  // STEP 3: Password setup
  if (step === 'password') {
    return (
      <div className="auth-page">
        <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
        <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80 z-[1]" />
        <div className="auth-page-content items-center px-5">
          {/* Glass card */}
          <div className="w-full max-w-md backdrop-blur-xl bg-black/40 rounded-3xl p-6 border border-white/10 shadow-2xl">
            {/* Back Button */}
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-white/70 mb-4 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>{t('auth.back')}</span>
            </button>

            {/* Logo */}
            <div className="text-center mb-5">
              <img 
                src={LOGO_LIGHT} 
                alt="Slimmy"
                className="h-9 mx-auto mb-1.5 drop-shadow-lg"
              />
              <p className="text-white/90 text-sm font-medium">
                {t('auth.setPassword')}
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* Password Input */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password')}
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="register-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Confirm Password Input */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.confirmPassword')}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="register-confirm-password-input"
                  required
                  minLength={6}
                />
              </div>

              {/* Password requirements */}
              <p className="text-white/50 text-xs">
                {t('auth.passwordMinLength')}
              </p>

              {/* Error */}
              {error && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!password || !confirmPassword || loading}
                className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  password && confirmPassword && !loading
                    ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="register-password-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  t('auth.completeRegistration')
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2: Code entry
  if (step === 'code') {
    return (
      <div className="auth-page">
        <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
        <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80 z-[1]" />
        <div className="absolute top-[max(env(safe-area-inset-top),16px)] right-4 z-[55]">
          <LanguageSelectorBadge />
        </div>
        <div className="auth-page-content items-center px-5">
          {/* Glass card */}
          <div className="w-full max-w-md backdrop-blur-xl bg-black/40 rounded-3xl p-6 border border-white/10 shadow-2xl mb-8">
            {/* Back Button */}
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-white/70 mb-4 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>{t('auth.back')}</span>
            </button>

            {/* Logo & Title */}
            <div className="text-center mb-6">
              <img 
                src={LOGO_LIGHT} 
                alt="Slimmy"
                className="h-9 mx-auto mb-3 drop-shadow-lg"
              />
              <h2 className="text-xl font-bold text-white mb-2">{t('auth.enterCode')}</h2>
              <p className="text-white/70 text-sm">
                {t('auth.codeSentTo')}<br />
                <strong className="text-white">{email}</strong>
              </p>
            </div>

            <form onSubmit={handleCodeSubmit} className="space-y-5">
              {/* 6-digit Code Input - Taller rectangular boxes */}
              <div className="flex justify-center gap-3" onPaste={handleCodePaste}>
                {code.map((digit, index) => (
                  <input
                    key={`code-${index}`}
                    ref={(el) => (codeInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="w-11 h-14 text-center text-2xl font-bold rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                    style={{ aspectRatio: '3/4' }}
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {/* Success message for resend */}
              {resendSuccess && (
                <div className="bg-green-500/20 text-green-200 p-3 rounded-xl text-sm text-center border border-green-500/30">
                  {t('auth.newCodeSent')}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={code.join('').length !== 6 || loading}
                className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  code.join('').length === 6 && !loading
                    ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  t('auth.verifyCode')
                )}
              </button>
            </form>

            {/* Resend code */}
            <div className="text-center mt-5">
              <p className="text-white/50 text-sm mb-2">{t('auth.didntReceiveCode')}</p>
              <button
                onClick={handleResendCode}
                disabled={resendLoading}
                className="text-white font-medium text-sm hover:underline flex items-center justify-center gap-2 mx-auto"
              >
                {resendLoading ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <RefreshCw size={16} />
                )}
                {t('auth.resendCode')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STEP 1: Email entry (default)
  return (
    <div className="auth-page">
      <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
      <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80 z-[1]" />
      <div className="auth-page-content items-center">
        <div className="px-5 pb-4 w-full max-w-md">
          {/* Glass card */}
          <div className="backdrop-blur-xl bg-black/25 rounded-3xl p-6 border border-white/10 shadow-2xl">
            {/* Logo */}
            <div className="text-center mb-5">
              <img 
                src={LOGO_LIGHT} 
                alt="Slimmy"
                className="h-9 mx-auto mb-1.5 drop-shadow-lg"
              />
              <p className="text-white/90 text-sm font-medium">
                {t('auth.createAccount')}
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              {/* Email Input */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.yourEmail')}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="register-email-input"
                  required
                />
              </div>

              {/* Terms checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <div 
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all mt-0.5 ${
                    acceptedTerms 
                      ? 'bg-[#E8334A] border-[#E8334A]' 
                      : 'border-white/40 bg-white/10'
                  }`}
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                >
                  {acceptedTerms && <Check size={16} className="text-white" />}
                </div>
                <span className="text-white/70 text-sm leading-relaxed">
                  {t('auth.acceptTerms')}{' '}
                  <a href="/terms" className="text-white underline" target="_blank">
                    {t('auth.terms')}
                  </a>{' '}
                  {t('auth.and')}{' '}
                  <a href="/privacy" className="text-white underline" target="_blank">
                    {t('auth.privacyPolicy')}
                  </a>
                </span>
              </label>

              {/* Error */}
              {error && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!email || !acceptedTerms || loading}
                className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  email && acceptedTerms && !loading
                    ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  t('auth.continue')
                )}
              </button>
            </form>

            {/* Google login option */}
            {googleLoginEnabled && (
              <>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-white/20"></div>
                  <span className="text-white/50 text-sm">{t('auth.or')}</span>
                  <div className="flex-1 h-px bg-white/20"></div>
                </div>
                
                <button
                  onClick={async () => {
                    try {
                      if (isNative) {
                        await browser.open(`${API}/api/auth/google/login?source=app`);
                      } else {
                        window.location.href = `${API}/api/auth/google/login`;
                      }
                    } catch (err) {
                      console.error('[GoogleLogin] Login/register failed:', err);
                      setError(t('auth.googleLoginError') || 'Errore durante il login con Google. Riprova.');
                    }
                  }}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-white text-gray-800 flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                  data-testid="google-register-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('auth.continueWithGoogle')}
                </button>
              </>
            )}

            {/* Login link */}
            <p className="text-white/60 text-sm mt-4 text-center">
              {t('auth.hasAccount')}{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-white font-medium hover:underline"
              >
                {t('auth.login')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
